/**
 * Render-scale governor (09 §6): the GL backbuffer is device px / r. When the p95 frame time misses
 * the budget for 2 s, r steps up 1 → 1.25 → 1.5 → 2 (→ 3 → 4 at f ≥ 3), capped at max(f, 1.5).
 * After 10 s within budget it steps back down one step, but only if the predicted p95 at the lower
 * step (fill-bound model: p95 · (r / r_next)²) stays ≤ 0.85 · budget. A step-down that has to be
 * undone (stepped up again within the up-window after it) backs the down-delay off ×3
 * (10 → 30 → 90 s). A setting can pin r.
 *
 * Pure logic, driven by `sample(frameMs, nowSec)` once per frame; allocation-free.
 * `r = f` renders GL at stage resolution (still the exact reference look); `r > f` (below f = 1.5)
 * is a labelled performance mode. `u` below is the UI scale `f` (device px per stage px), a real number.
 */

export interface GovernorOptions {
  /** frame budget in ms (05: 16.6 dGPU, 22 integrated) */
  budgetMs: number;
  /** UI scale f (device px per stage px), any positive real */
  u: number;
  /** frames in the p95 window (default 60, about 1 s at 60 fps) */
  windowFrames?: number;
  /** seconds over budget before stepping up (default 2) */
  upAfterSec?: number;
  /** seconds within budget before stepping down (default 10) */
  downAfterSec?: number;
  /** pinned r (disables the governor), or null */
  pin?: number | null;
}

/** Base steps; integer steps up to u are appended at high u (8K: r = 4 allowed). */
export const GOVERNOR_STEPS: readonly number[] = [1, 1.25, 1.5, 2];

/** The step ladder for a UI scale u = f: base steps plus integers up to u, capped at max(u, 1.5). */
export function governorSteps(u: number): number[] {
  const cap = Math.max(u, 1.5);
  const s = GOVERNOR_STEPS.filter((r) => r <= cap + 1e-9);
  for (let i = 3; i <= Math.floor(cap); i++) if (!s.includes(i)) s.push(i);
  return s.sort((a, b) => a - b);
}

export class RenderScaleGovernor {
  private budget: number;
  private steps: number[];
  private stepIdx = 0;
  private readonly win: Float64Array;
  private readonly sorted: Float64Array;
  private n = 0;
  private head = 0;
  private overSince = Number.NaN;
  private underSince = Number.NaN;
  private upAfter: number;
  private downAfter: number;
  private pinned: number | null;
  private lastP95 = 0;
  private downDelay: number;
  private lastDownAt = Number.NaN;
  /** steps taken (for the debug overlay and tests) */
  changes = 0;

  constructor(opts: GovernorOptions) {
    this.budget = opts.budgetMs;
    this.steps = governorSteps(opts.u);
    const w = Math.max(5, opts.windowFrames ?? 60);
    this.win = new Float64Array(w);
    this.sorted = new Float64Array(w);
    this.upAfter = opts.upAfterSec ?? 2;
    this.downAfter = opts.downAfterSec ?? 10;
    this.downDelay = this.downAfter;
    this.pinned = opts.pin ?? null;
  }

  /** Current render scale r. */
  get r(): number {
    return this.pinned ?? this.steps[this.stepIdx] ?? 1;
  }
  get p95(): number {
    return this.lastP95;
  }
  get isPinned(): boolean {
    return this.pinned !== null;
  }
  /** at the top step (or pinned): nothing left for the governor to give, so LITE may act */
  get atCap(): boolean {
    return this.pinned !== null || this.stepIdx >= this.steps.length - 1;
  }
  get ladder(): readonly number[] {
    return this.steps;
  }

  setBudget(ms: number): void {
    this.budget = ms;
  }

  /** New UI scale (resize): new ladder, keep r if it is still on it, else the nearest lower step. */
  setU(u: number): void {
    const r = this.steps[this.stepIdx] ?? 1;
    this.steps = governorSteps(u);
    let idx = 0;
    for (let i = 0; i < this.steps.length; i++) if ((this.steps[i] ?? 1) <= r + 1e-9) idx = i;
    this.stepIdx = idx;
    this.resetWindow();
  }

  /** Pin r (a setting), or null to let the governor run. */
  pin(r: number | null): void {
    this.pinned = r;
    this.resetWindow();
  }

  resetWindow(): void {
    this.n = 0;
    this.head = 0;
    this.overSince = Number.NaN;
    this.underSince = Number.NaN;
  }

  /** p95 of the current window (allocation-free: sorts a preallocated copy). */
  private computeP95(): number {
    const n = this.n;
    if (n === 0) return 0;
    const s = this.sorted;
    for (let i = 0; i < s.length; i++) s[i] = i < n ? (this.win[i] ?? 0) : Infinity; // unused slots sort last
    s.sort();
    const idx = Math.min(n - 1, Math.ceil(0.95 * n) - 1);
    return s[idx] ?? 0;
  }

  /**
   * Feed one frame time (ms) at wall time `nowSec`. Returns the (possibly new) r; the caller
   * applies it (engine.applyScale) when it changes.
   */
  sample(frameMs: number, nowSec: number): number {
    if (this.pinned !== null) return this.pinned;
    this.win[this.head] = frameMs;
    this.head = (this.head + 1) % this.win.length;
    if (this.n < this.win.length) this.n++;
    // need a reasonably full window before judging
    if (this.n < Math.min(this.win.length, 30)) return this.r;
    const p95 = this.computeP95();
    this.lastP95 = p95;
    if (p95 > this.budget) {
      this.underSince = Number.NaN;
      if (Number.isNaN(this.overSince)) this.overSince = nowSec;
      if (nowSec - this.overSince >= this.upAfter && this.stepIdx < this.steps.length - 1) {
        // undoing a recent step-down: back the down-delay off (10 → 30 → 90 s)
        if (!Number.isNaN(this.lastDownAt) && nowSec - this.lastDownAt <= this.upAfter * 3 + 1) {
          this.downDelay = Math.min(this.downDelay * 3, this.downAfter * 9);
        }
        this.lastDownAt = Number.NaN;
        this.stepIdx++;
        this.changes++;
        this.resetWindow();
      }
    } else {
      this.overSince = Number.NaN;
      if (Number.isNaN(this.underSince)) this.underSince = nowSec;
      if (nowSec - this.underSince >= this.downDelay && this.stepIdx > 0) {
        const rCur = this.steps[this.stepIdx] ?? 1;
        const rNext = this.steps[this.stepIdx - 1] ?? 1;
        const predicted = p95 * (rCur / rNext) ** 2;
        if (predicted <= 0.85 * this.budget) {
          this.stepIdx--;
          this.changes++;
          this.lastDownAt = nowSec;
          this.resetWindow();
        } else {
          this.underSince = nowSec; // re-check after another down-delay
        }
      }
    }
    return this.r;
  }
}
